// The app's badge: what the parts count as waiting for the person
// (GET /api/me/badge). Read again on the events that can change it.
//
// The Inbox's rows sit under the same key (inbox/queries.ts). They are read
// again even while the Inbox is not shown: the person leaves it to open a row
// and comes back, and the row they settled must already be gone then, not
// leave in front of them.
//
// Events that change the count come in bursts (a run ending writes its reply,
// its run and its chat at once): the reads are put off a moment and made once
// for the burst, after its last event.
import { type QueryClient, queryOptions } from "@tanstack/react-query";
import type { InboxEntry } from "@trameria/core/notices-shared";
import type { Api } from "../data/api";
import { invalidateAfterBurst } from "../realtime/settle";

export const badgeKey = ["me", "badge"] as const;

/** What the Inbox shows as waiting (inbox/queries.ts reads it). */
export const waitingPrefix = [...badgeKey, "inbox"] as const;

export const badgeQuery = (api: Api) =>
  queryOptions({
    queryKey: badgeKey,
    queryFn: () => api<{ count: number }>("/api/me/badge"),
  });

export const badgeChanged = (qc: QueryClient) =>
  invalidateAfterBurst(qc, { queryKey: badgeKey, refetchType: "all" });

/**
 * The person settled here what Inbox rows wait for (read the chat, answered
 * the item): `keys` (`<kind>:<id>`) leave the kept rows at once, before the
 * server's event reads them again. A read already under way is stopped first
 * (it would put them back).
 */
export async function inboxRowsSettled(
  qc: QueryClient,
  keys: readonly string[],
) {
  await qc.cancelQueries({ queryKey: waitingPrefix });
  qc.setQueriesData<InboxEntry[]>({ queryKey: waitingPrefix }, (rows) =>
    rows?.filter((r) => !keys.includes(r.key)),
  );
}
